import { Http } from 'Utils/Http';
import { IRateioAdapter } from 'Data/interfaces/Faturamento/IRateioPort';
import { IBaseResponse } from 'Data/interfaces/BaseResponse';
import {
  BillingModeEnum,
  IBillingSplitResponse,
  IRateioResponse,
  IRateioUpdateRequest,
} from 'Data/interfaces/Faturamento/IRateio';
import { ISpeRequest, ISpeResponse } from 'Data/interfaces/Faturamento/ISpe';

// Adaptador HTTP real, contra BillingSplitController (api, ControllersV2).
// Ligado por REACT_APP_RATEIO_USE_MOCK=false — ver ./index.ts.
//
// Escritas exigem admin do tenant: o backend responde 400 NOTAUTHORIZED para os
// demais papéis. Regras de negócio violadas (soma diferente de 100, CNPJ duplicado,
// remoção da última SPE em rateio) também voltam como 400, com o código do erro.

const BASE = '/v2/billing-split';

// Rotas do cliente em BASE; as do painel /adm, idênticas, em BASE/tenants/{id}.
// Estas exigem o Admin da aplicação (role Identity), não o do tenant.
const baseFor = (tenantId?: number): string =>
  tenantId ? `${BASE}/tenants/${tenantId}` : BASE;

export class RateioHttpAdapter implements IRateioAdapter {
  async getBillingSplit(tenantId?: number): Promise<IBillingSplitResponse> {
    const axios = await Http.axios();
    const response = await axios.get<IBaseResponse<IBillingSplitResponse>>(
      baseFor(tenantId)
    );
    return response.data.data;
  }

  async setBillingMode(
    mode: BillingModeEnum,
    tenantId?: number
  ): Promise<IBillingSplitResponse> {
    const axios = await Http.axios();
    const response = await axios.patch<IBaseResponse<IBillingSplitResponse>>(
      `${baseFor(tenantId)}/mode`,
      { mode }
    );
    return response.data.data;
  }

  async addSpe(spe: ISpeRequest, tenantId?: number): Promise<ISpeResponse> {
    const axios = await Http.axios();
    const response = await axios.post<IBaseResponse<ISpeResponse>>(
      `${baseFor(tenantId)}/spes`,
      spe
    );
    return response.data.data;
  }

  async updateSpe(
    speId: string,
    spe: ISpeRequest,
    tenantId?: number
  ): Promise<ISpeResponse> {
    const axios = await Http.axios();
    const response = await axios.put<IBaseResponse<ISpeResponse>>(
      `${baseFor(tenantId)}/spes/${speId}`,
      spe
    );
    return response.data.data;
  }

  async removeSpe(speId: string, tenantId?: number): Promise<IBillingSplitResponse> {
    const axios = await Http.axios();
    const response = await axios.delete<IBaseResponse<IBillingSplitResponse>>(
      `${baseFor(tenantId)}/spes/${speId}`
    );
    return response.data.data;
  }

  async updateRateio(
    request: IRateioUpdateRequest,
    tenantId?: number
  ): Promise<IRateioResponse> {
    const axios = await Http.axios();
    const response = await axios.put<IBaseResponse<IRateioResponse>>(
      `${baseFor(tenantId)}/rateio`,
      request
    );
    return response.data.data;
  }

  // Fica em TermsAcceptanceController, não em BillingSplitController.
  async resendTermsAcceptance(speId?: string, tenantId?: number): Promise<void> {
    const axios = await Http.axios();
    const path = tenantId
      ? `/v2/terms-acceptances/tenants/${tenantId}/billing-split/resend`
      : '/v2/terms-acceptances/billing-split/resend';
    await axios.post(path, { speId: speId ?? null });
  }
}
